import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button } from "@postdaily/ui/components/button";
import { Spinner } from "@postdaily/ui/components/spinner";
import { createFileRoute, Navigate, Outlet } from "@tanstack/react-router";

import { AnalyticsIdentity } from "@/components/analytics/analytics-identity";
import {
  WorkspaceGate,
  WorkspaceProvider,
} from "@/hooks/use-current-workspace";
import { authClient } from "@/lib/auth-client";
import { createNoIndexHead } from "@/lib/seo";

export const Route = createFileRoute("/(onboarding)")({
  // First-run wizard is authenticated and has no SEO value — client-only.
  ssr: false,
  head: () => createNoIndexHead("Onboarding — PostDaily"),
  component: RouteComponent,
});

/**
 * Dedicated layout for the full-screen first-run wizard. It shares the session
 * guard and workspace bootstrapping with `(app)` but intentionally omits the
 * `AppShell`, so new users land straight in onboarding without a flash of the
 * dashboard chrome.
 */
function RouteComponent() {
  const { data: session, isPending, error, refetch } = authClient.useSession();

  if (isPending) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background p-6">
        <Spinner className="size-10" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background p-6">
        <Alert variant="destructive" className="max-w-md">
          <AlertTitle>Could not check your session</AlertTitle>
          <AlertDescription>
            We could not confirm your sign-in state. Try again to continue.
          </AlertDescription>
          <AlertAction>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                void refetch();
              }}
            >
              Try again
            </Button>
          </AlertAction>
        </Alert>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" />;
  }

  return (
    <WorkspaceProvider>
      {/* Identify here too: the wizard is where a new user's first events
          happen, and without it they stay anonymous and untagged until the
          app shell mounts. */}
      <AnalyticsIdentity />
      <WorkspaceGate>
        <Outlet />
      </WorkspaceGate>
    </WorkspaceProvider>
  );
}
